import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import {
  HD_EMOTIONAL_WAVES,
  HD_EMOTIONAL_WAVE_VIEWBOX,
  HdEmotionalWaveKey,
  HdEmotionalWaveRef,
  getHdEmotionalWave,
  hdEmotionalWaveColor,
} from './hd-emotional-waves.metadata';

interface WaveStrokeVm {
  readonly key: HdEmotionalWaveKey;
  readonly path: string;
  readonly color: string;
  /** Verticale band (0..2) bij de gestapelde bron-weergave; `null` = volle hoogte. */
  readonly band: number | null;
  readonly highlighted: boolean;
  readonly dimmed: boolean;
}

/**
 * Mini-afbeelding van een emotionele golf: de circuitvorm als lijn in de
 * circuitkleur. Voor poort 6 (`'source'`) drie gestapelde golfjes (tribaal,
 * individueel, collectief); de golven in {@link highlight} staan vol, de
 * andere gedimd. Zonder uitlichting staan ze alle drie gelijk.
 */
@Component({
  selector: 'hd-emotional-wave-glyph',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.hd-emotional-wave-glyph--large]': "size() === 'large'",
    '[class.hd-emotional-wave-glyph--source]': "wave() === 'source'",
    '[attr.data-wave]': 'wave()',
  },
  template: `
    <svg
      [attr.viewBox]="viewBox"
      preserveAspectRatio="none"
      [attr.role]="label() ? 'img' : null"
      [attr.aria-label]="label() || null"
      [attr.aria-hidden]="label() ? null : 'true'"
      focusable="false"
    >
      @for (stroke of strokes(); track stroke.key) {
        <path
          class="hd-emotional-wave-glyph__stroke"
          [class.is-highlighted]="stroke.highlighted"
          [class.is-dimmed]="stroke.dimmed"
          [attr.data-stroke]="stroke.key"
          [attr.d]="stroke.path"
          [attr.stroke]="stroke.color"
          [attr.transform]="stroke.band === null ? null : 'translate(0 ' + stroke.band * 12 + ') scale(1 0.3333)'"
        />
      }
    </svg>
  `,
  styles: `
    :host {
      display: inline-block;
      width: 54px;
      height: 16px;
      flex: 0 0 auto;
      line-height: 0;
    }

    :host(.hd-emotional-wave-glyph--large) {
      width: 132px;
      height: 40px;
    }

    /* Drie gestapelde golfjes hebben meer hoogte nodig om leesbaar te blijven. */
    :host(.hd-emotional-wave-glyph--source) {
      height: 26px;
    }

    :host(.hd-emotional-wave-glyph--large.hd-emotional-wave-glyph--source) {
      height: 60px;
    }

    svg {
      width: 100%;
      height: 100%;
      overflow: visible;
    }

    .hd-emotional-wave-glyph__stroke {
      fill: none;
      stroke-width: 2.2px;
      stroke-linejoin: round;
      stroke-linecap: round;
      /* Gelijke lijndikte, ook in de samengedrukte banden van poort 6. */
      vector-effect: non-scaling-stroke;
    }

    .hd-emotional-wave-glyph__stroke.is-highlighted {
      stroke-width: 2.6px;
    }

    .hd-emotional-wave-glyph__stroke.is-dimmed {
      opacity: 0.3;
      stroke-width: 1.4px;
    }
  `,
})
export class HdEmotionalWaveGlyphComponent {
  readonly wave = input.required<HdEmotionalWaveRef>();
  /** Enkel bij `'source'`: de golven die vol getekend worden. */
  readonly highlight = input<readonly HdEmotionalWaveKey[]>([]);
  readonly size = input<'mini' | 'large'>('mini');
  /** Toegankelijke naam; leeg = decoratief (aria-hidden). */
  readonly label = input('');

  protected readonly viewBox = HD_EMOTIONAL_WAVE_VIEWBOX;

  protected readonly strokes = computed<readonly WaveStrokeVm[]>(() => {
    const wave = this.wave();
    if (wave !== 'source') {
      const definition = getHdEmotionalWave(wave);
      return [{
        key: definition.key,
        path: definition.path,
        color: hdEmotionalWaveColor(definition.key),
        band: null,
        highlighted: false,
        dimmed: false,
      }];
    }
    const highlight = this.highlight();
    return HD_EMOTIONAL_WAVES.map((definition, band) => ({
      key: definition.key,
      path: definition.path,
      color: hdEmotionalWaveColor(definition.key),
      band,
      highlighted: highlight.includes(definition.key),
      dimmed: highlight.length > 0 && !highlight.includes(definition.key),
    }));
  });
}
